import { RequestClientService, ResponseType } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { autoinject } from "aurelia-framework";
import { RightShipInspectionRoutes } from "../../constants/rightship-inspection/rightship-inspection.constants";
import {
    RightShipInspectionClientModel,
    RightShipInspectionModel,
    SaveRightShipInspectionResponse,
    SaveRightShipInspectionRequest,
    ChangeRightShipInspectionValidityDateRequest,
    ChangeRightShipInspectionValidityDateResponse,
    GetRequiredRightShipInspectionResponse,
    SendInspectionEmailRequest,
    ChangeInspectionCompanyRequest,
    GetInspectorReportResponse,
    GetInspectorReportRequest,
    GetCrewMatricesByInspectionIdResponse,
    GetCrewMatricesByInspectionIdRequest,
    GetRSInspectionNonConformityResponse,
    ChangeInspectionWorkflowRequest,
    RightShipInspectionExecutiveSummaryResponse,
    RSInspectionCompanyResponseModel,
    SubmitRSInspectionCompanyResponse,
    SubmitRSInspectionCompanyRequest,
    SaveRightShipInspectionBillingDetailsRequest,
    SaveFindingGradingRequest,
    AssignInspectorAssignmentRequest,
    InviteInspectorRequest,
    InviteInspectorResponse,
    ResendBookingRequestRequest,
    ResendAcceptanceEmailsRequest,
    GetFindingExtractionStatusResponse,
    GetRSInspectionOutcomeResponse,
    RestoreFindingToAIContentRequest,
    RestoreFindingToAIContentReponse,
    GetRightShipInspectionDocumentLinksResponse,
    ResendInvoiceEmailRequest,
    InspectionOutcomeFindingSummaryResponse,
    ComputeInspectionValidityResponse,
    GetAiInsightResponse
} from "../../models/rightship-inspections/rightship-inspections.models";
import { SubtaskCorrespondenceEmailModel } from "../../models/vetting/vetting.models";
import { UnitOfWorkItem } from "../../utilities/unit-of-work-observer";
import { BaseValidationResponse } from "../../models/validations/validations.models";
import { Guid } from "../../utilities/guid";
import { GetInsightResponse } from "@rightship-tech/rs-inspections-ui/models/rightship-inspection.models";

@autoinject
export class RightShipInspectionsService {

    constructor(private requestClient: RequestClientService) { }

    public getRightShipInspectionClientModelFromGuid(guid: string): Promise<RightShipInspectionClientModel> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRightShipInspectionClientModelFromGuid}/${guid}`, {});
    }

    public getRightShipInspectionClientModel(id: number): Promise<RightShipInspectionClientModel> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRightShipInspectionClientModel}/${id}`, {});
    }

    public getRequiredRightShipInspection(vesselId: number, rightShipInspectionTypeId: number, includeDetails: boolean): Promise<GetRequiredRightShipInspectionResponse> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRequiredRightShipInspection}`, { vesselId, rightShipInspectionTypeId, includeDetails });
    }

    public getRightShipInspection(id: number): Promise<RightShipInspectionModel> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRightShipInspection}/${id}`, {});
    }

    public getRightShipInspectionByGuid(guid: string): Promise<RightShipInspectionModel> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRightShipInspectionByGuid}/${guid}`, {});
    }

    public saveRightShipInspection(request: SaveRightShipInspectionRequest): Promise<SaveRightShipInspectionResponse> {
        return this.requestClient.postFormData(RightShipInspectionRoutes.SaveRightShipInspection, request);
    }

    public changeRightShipInspectionValidityDate(request: ChangeRightShipInspectionValidityDateRequest): Promise<ChangeRightShipInspectionValidityDateResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.ChangeRightShipInspectionValidityDate, {}, request);
    }

    public sendInspectionEmail(request: SendInspectionEmailRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.SendRightShipInspectionEmail, {}, request);
    }

    public sendCrewMatrixInspectionEmail(request: SendInspectionEmailRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.SendCrewMatrixInspectionEmail, {}, request);
    }

    public updateRightShipInspectionReports(request): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.UpdateInspectionReports, {}, request);
    }

    public changeStatus(id: number,
        rightShipInspectionStatusId: number,
        statusName: string,
        statusSnippet: string,
        comments: string,
        meta: string,
        subtaskTypes: number[],
        emails: SubtaskCorrespondenceEmailModel[],
        changes: { [index: string]: UnitOfWorkItem }): Promise<BaseValidationResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.ChangeStatus, {}, {
            id: id,
            statusSnippet: statusSnippet,
            statusName,
            comments: comments,
            statusId: rightShipInspectionStatusId,
            meta: meta,
            subtaskTypes: subtaskTypes,
            emails: emails,
            changes: changes
        });
    }

    public createFormTask(inspectionId: number) : Promise<void>{
        return this.requestClient.get(RightShipInspectionRoutes.CreateFormTask, {inspectionId});
    }

    public changeInspectionCompany(request: ChangeInspectionCompanyRequest): Promise<BaseValidationResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.ChangeInspectionCompany, {}, request);
    }

    public changeRSInspectionInspector(request: ChangeInspectionCompanyRequest): Promise<BaseValidationResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.ChangeRSInspectionInspector, {}, request);
    }

    public getInspectorReport(request: GetInspectorReportRequest) : Promise<GetInspectorReportResponse>{
        return this.requestClient.get(RightShipInspectionRoutes.GetInspectorReport, request);
    }

    public GetCrewMatricesByInspectionId(request: GetCrewMatricesByInspectionIdRequest) : Promise<GetCrewMatricesByInspectionIdResponse>{
        return this.requestClient.get(RightShipInspectionRoutes.GetCrewMatricesByInspectionIdResponse, request);
    }

    public getRSInspectionNonConformity(inspectionId: number) : Promise<GetRSInspectionNonConformityResponse>{
        return this.requestClient.get(RightShipInspectionRoutes.GetRSInspectionNonConformity, {rightShipInspectionId: inspectionId});
    }

    public getRSInspectionOutcome(inspectionId: number) : Promise<GetRSInspectionOutcomeResponse>{
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRSInspectionOutcome}/${inspectionId}`);
    }
    
    public changeInspectionWorkflow(request: ChangeInspectionWorkflowRequest) : Promise<void>{
        return this.requestClient.post(RightShipInspectionRoutes.ChangeInspectionWorkflow,  {}, request);
    }

    public getInspectionReportPdf(inspectionId: number): Promise<void> {
        const uri = `${RightShipInspectionRoutes.DownloadInspectionInspectorReportPdf}`;

        return this.requestClient.get<Blob>(uri, {rightShipInspectionId: inspectionId}, ResponseType.Blob).then(x => {
            this.requestClient.downloadFile(x, "RightShip Inspection Inspector Report" + ".pdf");
        }, e => { throw e; });
    }

    public getRightShipInspectionExecutiveSummary(inspectionId: number) : Promise<RightShipInspectionExecutiveSummaryResponse>{
        return this.requestClient.get(RightShipInspectionRoutes.GetRightShipInspectionExecutiveSummary, {rightShipInspectionId: inspectionId});
    }

    public checkAlreadySubmittedRSInspectionCompanyResponse(guid: Guid) : Promise<RSInspectionCompanyResponseModel>{
        return this.requestClient.get(`${RightShipInspectionRoutes.CheckAlreadySubmittedRSInspectionCompanyResponse}/${guid}`, {});
    }

    public submitRSInspectionCompanyResponse(request: SubmitRSInspectionCompanyRequest): Promise<SubmitRSInspectionCompanyResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.SubmitRSInspectionCompanyResponse, {}, request);
    }

    public saveRightShipInspectionBillingDetails(request: SaveRightShipInspectionBillingDetailsRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.SaveRightShipInspectionBillingDetails, {}, request);
    }

    public resendInspectionRequiredEmail(id: number,
        correspondenceMessage: string,        
        changes: { [index: string]: UnitOfWorkItem }): Promise<BaseValidationResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.ResendInspectionRequiredEmail, {}, {
            id: id,
            correspondenceMessage: correspondenceMessage,
            changes: changes
        });
    }
    
    public restoreFindingToAIContent(request: RestoreFindingToAIContentRequest): Promise<RestoreFindingToAIContentReponse> {
        return this.requestClient.post(RightShipInspectionRoutes.RestoreFindingToAIContent, {}, request);
    }

    public saveFindingGrading(request: SaveFindingGradingRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.SaveFindingGrading, {}, request);
    }

    public assignInspectorAssignment(request: AssignInspectorAssignmentRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.AssignInspectorAssignment, {}, request);
    }
    
    public InviteInspector(request: InviteInspectorRequest): Promise<InviteInspectorResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.InviteInspector, {}, request);
    }

    public resendBookingRequest(request: ResendBookingRequestRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.ResendBookingRequest, {}, request);
    }

    public changeAssignedInspector(request: InviteInspectorRequest): Promise<InviteInspectorResponse> {
        return this.requestClient.post(RightShipInspectionRoutes.ChangeAssignedInspectors, {}, request);
    }

    public resendAcceptanceEmails(request: ResendAcceptanceEmailsRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.ResendAcceptanceEmails, {}, request);
    }

    public resendInvoiceEmail(request: ResendInvoiceEmailRequest): Promise<void> {
        return this.requestClient.post(RightShipInspectionRoutes.ResendInvoiceEmail, {}, request);
    }

    public getFindingExtractionStatus(inspectionId: number): Promise<GetFindingExtractionStatusResponse> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetFindingExtractionStatus}/${inspectionId}`);
    }
    
    public getInspectionThumbnailZipFile(aiRunId: string): Promise<any> {
        return this.requestClient.get<Blob>(`${RightShipInspectionRoutes.GetInspectionThumbnailZipFile}/${aiRunId}`, {}, ResponseType.Blob)
    }

    public getRightShipInspectionDocumentLinks(inspectionId: number): Promise<GetRightShipInspectionDocumentLinksResponse> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetRightShipInspectionDocumentLinks}/${inspectionId}`);
    }

    public getFindingAllDocumentsCount(inspectionId: number): Promise<number> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetFindingAllDocumentsCount}/${inspectionId}`);
    }

    public getInsights(inspectionId: number): Promise<GetInsightResponse> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetInsights}/${inspectionId}`);
    }

    public getAiInsights(inspectionId: number): Promise<GetAiInsightResponse> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetAiInsights}/${inspectionId}`);
    }

    public getInspectionOutcomeFindingSummary(inspectionId: number): Promise<InspectionOutcomeFindingSummaryResponse> {
        return this.requestClient.get(`${RightShipInspectionRoutes.GetInspectionOutcomeFindingSummary}/${inspectionId}`);
    }

    public computeValidity(rsInspectionId: number, reportQualityId: number): Promise<ComputeInspectionValidityResponse> {
        return this.requestClient.post(`${RightShipInspectionRoutes.ComputeValidity}`,{}, {rsInspectionId, reportQualityId});
    }

}
